import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router-dom";
import type { Vessel } from "@common/types";
import { getLogbookList } from "../services/api";
import { useAuthStore } from "../stores/auth-store";
import { useVesselStore } from "../stores/vessel-store";

export function resolveVessel(vessels: Vessel[], requested?: string | null, saved?: string) {
  const active = vessels.filter(v => v.isActive);
  // An invalid explicit link must never silently open another vessel for recording.
  if (requested) return active.find(v => v.id === requested);
  return active.find(v => v.id === saved) ?? active[0];
}

export const vesselScopedRoute = (pathname: string, params: URLSearchParams) => pathname.startsWith("/logbook") || pathname === "/maintenance" || (pathname === "/admin/logbook" && (!params.get("tab") || params.get("tab") === "vessels"));

export function useSelectedVessel() {
  const userId = useAuthStore(s => s.user?.id);
  const saved = useVesselStore(s => userId ? s.byUser[userId] : undefined);
  const select = useVesselStore(s => s.select);
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const scoped = vesselScopedRoute(pathname, params);
  const query = useQuery({ queryKey: ["logbook", "vessels"], queryFn: () => getLogbookList("vessels"), staleTime: 60_000, refetchInterval: 60_000 });
  const vessels = query.data?.data ?? [];
  const vessel = resolveVessel(vessels, scoped ? params.get("vesselId") : null, saved);
  const selectedId = vessel?.id;
  useEffect(() => {
    if (userId && selectedId && selectedId !== saved) select(userId, selectedId);
  }, [userId, selectedId, saved, select]);
  return { vessel, vessels: vessels.filter(v => v.isActive), query, select: (id: string) => { if (userId) select(userId, id); } };
}
